import { create } from 'zustand';
import {
  listTeams as apiList,
  getTeam as apiGet,
  upsertTeam as apiUpsert,
  deleteTeam as apiDelete,
  listAuditEntries as apiAudit,
} from '../services/api/doctorTeamApi';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * Doctor Team Store (Phase 2.7)
 *
 * Holds the full team roster the caller can see:
 *   - Admin: every doctor's team.
 *   - Doctor: their own team only (or empty when no team is configured).
 *   - Nurse / receptionist: don't have access (UI gates them out).
 *
 * Re-fetches on every mutation rather than patching — same pattern as
 * procedureStore. Cheap at clinic-scale row counts.
 *
 * `teamsByDoctor` is a derived map keyed by doctor_member_id for O(1)
 * lookup from the Manage Staff inline editor + the AttachPlanModal
 * assignee picker.
 * ═══════════════════════════════════════════════════════════════════════
 */
const useDoctorTeamStore = create((set, get) => ({
  teams: [],
  teamsByDoctor: {},
  isLoading: false,
  // Phase 2.7 fix — flips true after the first fetchTeams() resolves
  // (success OR failure), regardless of how many teams exist. Lets
  // consumers (TeamAssignmentPicker fallback, TreatmentPlansPage
  // formReady) distinguish "teams genuinely empty / default-open" from
  // "still loading, don't fall back yet."
  teamsLoaded: false,
  error: null,

  // Audit trail is per-doctor, lazily loaded on demand by the
  // ManageStaff "View audit" expandable. Cached by doctor id.
  auditByDoctor: {},
  auditLoadingDoctorId: null,

  fetchTeams: async () => {
    set({ isLoading: true, error: null });
    const r = await apiList();
    if (r.success) {
      const teams = r.data?.teams || [];
      const map = {};
      for (const t of teams) {
        map[t.doctor_member_id] = t;
      }
      set({ teams, teamsByDoctor: map, isLoading: false, teamsLoaded: true });
    } else {
      // Mark loaded even on failure so the UI doesn't hang waiting; the
      // default-open fallback (anyone in clinic) is the safer behaviour
      // when we can't reach the team endpoint.
      set({ error: r.error, isLoading: false, teamsLoaded: true });
    }
    return r;
  },

  fetchTeam: async (doctorMemberId) => {
    const r = await apiGet(doctorMemberId);
    if (r.success && r.data) {
      const merged = { ...get().teamsByDoctor, [doctorMemberId]: r.data };
      const teams = Object.values(merged);
      set({ teams, teamsByDoctor: merged });
    }
    return r;
  },

  /**
   * Idempotent PUT: caller passes the COMPLETE members list. Server
   * diffs against existing row to emit per-member audit actions.
   *
   * @param {string} doctorMemberId
   * @param {{members: Array<{member_id, role, label?}>, reason?: string}} body
   */
  upsertTeam: async (doctorMemberId, body) => {
    const r = await apiUpsert(doctorMemberId, body);
    if (r.success) await get().fetchTeams();
    return r;
  },

  deleteTeam: async (doctorMemberId) => {
    const r = await apiDelete(doctorMemberId);
    if (r.success) await get().fetchTeams();
    return r;
  },

  fetchAudit: async (doctorMemberId) => {
    set({ auditLoadingDoctorId: doctorMemberId });
    const r = await apiAudit(doctorMemberId);
    if (r.success) {
      set({
        auditByDoctor: {
          ...get().auditByDoctor,
          [doctorMemberId]: r.data?.entries || [],
        },
        auditLoadingDoctorId: null,
      });
    } else {
      set({ auditLoadingDoctorId: null });
    }
    return r;
  },

  /**
   * Returns true when `candidateMemberId` is on `doctorMemberId`'s team
   * (or when no team exists — default-open rule). Mirrors the backend
   * DoctorTeamService.is_member_on_team semantics so the UI's
   * pre-validation matches what the server will accept.
   */
  isMemberOnTeam: (doctorMemberId, candidateMemberId) => {
    if (!candidateMemberId) return false;
    if (candidateMemberId === doctorMemberId) return true;
    const team = get().teamsByDoctor[doctorMemberId];
    if (!team) return true; // no team configured -> anyone allowed
    const members = team.members || [];
    return members.some((m) => m.member_id === candidateMemberId);
  },

  clearError: () => set({ error: null }),
}));

export default useDoctorTeamStore;
